// ---------------------------------------------------------------------------
// Data hooks for the Phase 1 screens.
//
// Every one of these calls a real endpoint that returns real rows. Nothing
// here invents data. Where a permission is missing the server simply refuses,
// and the hook is only mounted for people who hold that permission.
// ---------------------------------------------------------------------------

import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/apiClient';
import { queryKeys } from '../lib/queryClient';
import type {
  AuditEntry,
  DashboardSummary,
  Employee,
  ExchangeRate,
  Pagination,
  PermissionCatalogueItem,
  PinUser,
  RoleCatalogueItem,
} from '../types';

// --- login screen ----------------------------------------------------------

/// The employee tiles on the PIN pad. Public, because it has to load before
/// anyone has signed in. Returns names and roles only - no PIN, no hash, no
/// e-mail, no permissions.
///
/// FETCHED ON MOUNT, not when the PIN tab is opened, and that is the fix for
/// the delay. It used to be gated on the tab being active, which meant the
/// request only STARTED on the click - so the list arrived after a visible
/// pause, and a disabled query reports `isLoading: false`, so for that whole
/// pause the screen showed "No employee has a quick PIN yet" to a shop that
/// has four. The wrong message, at the only moment somebody reads it.
///
/// The endpoint is public and costs a few milliseconds, so there is nothing
/// to save by waiting. It is also independent of the catalogue, settings,
/// shift, printers and everything else the application loads: the sign-in
/// screen needs none of that, and must not wait for any of it.
export function usePinUsers() {
  return useQuery({
    queryKey: queryKeys.auth.pinUsers,
    queryFn: () => api.get<{ users: PinUser[] }>('/auth/pin-users'),
    staleTime: 5 * 60_000,
    // One retry, quickly. A sign-in screen that spends thirty seconds on
    // exponential backoff before admitting it failed is worse than one that
    // offers a button.
    retry: 1,
    retryDelay: 400,
  });
}

// --- application shell -----------------------------------------------------

/// The current USD to LBP rate shown in the header.
/// A missing rate is a normal state on a brand-new install, not an error, so a
/// 404 resolves to null instead of showing a broken header.
export function useExchangeRate() {
  return useQuery({
    queryKey: queryKeys.system.exchangeRate,
    queryFn: async () => {
      try {
        const response = await api.get<{ rate: ExchangeRate }>('/exchange-rate/current');
        return response.rate;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 2 * 60_000,
  });
}

// The "current shift" hook used to live here as well as in sales.api.ts.
//
// That was a real bug, not just duplication. Both hooks used the same query
// key, but one returned the shift unwrapped and the other returned
// `{ shift, totals }`. TanStack Query keeps ONE cache entry per key, so
// whichever component mounted first decided the shape, and the other one read
// it wrongly. In practice the header won, the POS then believed no shift was
// open, and the "Take payment" button stayed disabled forever - a cashier
// could not complete a sale at all.
//
// The rule this leaves behind: one query key, one hook, one shape. The single
// version now lives in sales.api.ts.

/// Shop name, receipt footer and similar. Readable by any signed-in employee.
export function usePublicSettings() {
  return useQuery({
    queryKey: queryKeys.system.publicSettings,
    queryFn: async () => {
      const response = await api.get<{ settings: Record<string, string> }>('/settings/public');
      return response.settings;
    },
    staleTime: 10 * 60_000,
  });
}

// --- dashboard -------------------------------------------------------------

export function useDashboardSummary(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.system.dashboard,
    queryFn: () => api.get<DashboardSummary>('/dashboard/summary'),
    enabled,
    staleTime: 30_000,
  });
}

// --- employees and permissions --------------------------------------------

export function useEmployees(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.system.employees,
    queryFn: async () => {
      const response = await api.get<{ employees: Employee[] }>('/employees');
      return response.employees;
    },
    enabled,
  });
}

export function usePermissionCatalogue(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.system.permissions,
    queryFn: () =>
      api.get<{ permissions: PermissionCatalogueItem[]; roles: RoleCatalogueItem[] }>('/permissions'),
    enabled,
    staleTime: 10 * 60_000,
  });
}

// --- audit log -------------------------------------------------------------

export function useAuditLog(page: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.system.audit({ page }),
    queryFn: () =>
      api.get<{ entries: AuditEntry[]; pagination: Pagination }>(`/audit?page=${page}&pageSize=25`),
    enabled,
    staleTime: 15_000,
  });
}
